import { useEffect, useRef } from "react";
import { useTheme } from "../../context/ThemeContext";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";

/**
 * Fondo del login y del registro: una escena 3D isométrica (almacén, camiones, colonia)
 * pre-renderizada como video en bucle. La escena se genera fuera de la app con
 * tools/escena-login (Three.js); aquí solo se reproduce, sin ninguna librería.
 *
 * - El video solo corre en web de escritorio: pantalla de 1024 px o más y mouse.
 *   En tablet (o con «reducir movimiento») queda la imagen fija; en celular no se pinta nada.
 * - Cada tema tiene su versión (de día y de noche) y solo se descarga la del tema activo.
 * - Se pausa con la pestaña oculta: no gasta batería decodificando algo que nadie ve.
 */
const BASE = `${import.meta.env.BASE_URL}login/escena`;

export default function EscenaLogin() {
  const { isDarkMode } = useTheme();
  const conPanel = useMediaQuery("(min-width: 768px)");
  const escritorio = useMediaQuery("(min-width: 1024px) and (pointer: fine)");
  const reducir = usePrefersReducedMotion();
  const video = useRef<HTMLVideoElement>(null);
  const tema = isDarkMode ? "noche" : "claro";
  const animar = escritorio && !reducir;

  useEffect(() => {
    if (!animar) return;
    const alCambiar = () => {
      const v = video.current;
      if (!v) return;
      if (document.hidden) v.pause();
      else v.play().catch(() => {}); // autoplay bloqueado: se queda la portada
    };
    document.addEventListener("visibilitychange", alCambiar);
    return () => document.removeEventListener("visibilitychange", alCambiar);
  }, [animar]);

  if (!conPanel) return null;

  return (
    <div className={`absolute inset-0 overflow-hidden ${isDarkMode ? "bg-[#0c1628]" : "bg-[#f5f7fb]"}`}>
      {animar ? (
        <video
          key={tema}
          ref={video}
          className="h-full w-full object-cover"
          poster={`${BASE}-${tema}.webp`}
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        >
          <source src={`${BASE}-${tema}.webm`} type="video/webm" />
          <source src={`${BASE}-${tema}.mp4`} type="video/mp4" />
        </video>
      ) : (
        <img key={tema} src={`${BASE}-${tema}.webp`} alt="" className="h-full w-full object-cover" />
      )}
    </div>
  );
}
